"use client"

import React, { useState, useRef, useEffect, Fragment } from "react"
import Link from "next/link"
import Image from "next/image"
import { isEmpty } from "lodash"
import { useRouter } from "next/navigation"
import clsx from "clsx/lite"
import { useClickAway } from "@uidotdev/usehooks"
import { twMerge } from "tailwind-merge"

const Item = ({
    href,
    title,
    subtitle = "",
    setNavigationMenuOpen = () => {},
}) => (
    <Link
        href={href}
        onClick={() => setNavigationMenuOpen(false)}
        className="block px-3.5 py-3 text-base border-l-4 border-l-white-snow hover:border-l-twilight-gray"
    >
        <strong className="block mb-1 font-bold text-caviar text-xl">
            {title}
        </strong>
        <span className="block font-medium leading-5 opacity-90 text-caviar">
            {subtitle}
        </span>
    </Link>
)

const Dropdown = ({ columns = [], setNavigationMenuOpen = () => {} }) => {
    return (
        <div className="flex items-stretch justify-center w-full max-w-2xl p-6 gap-x-3">
            {columns.map(({ header, items }, idx) => {
                if (items[idx].block) {
                    return (
                        <div
                            key={`image-${idx}`}
                            className="flex-shrink-0 w-48 bg-sticks-and-stones border-8 border-twilight-gray"
                        >
                            {items[idx].image && (
                                <Image
                                    src={`${process.env.NEXT_PUBLIC_STATIC_PREFIX}${items[idx].image.url}`}
                                    width={items[idx].image.width}
                                    height={items[idx].image.height}
                                    alt={items[idx].image.alt}
                                    priority
                                    className="w-full h-full bg-white-snow object-cover opacity-0 animate-[fadeIn_0.2s_ease-in-out_forwards] aspect-[4/3]"
                                />
                            )}
                        </div>
                    )
                }

                if (!items[idx].block) {
                    return (
                        <div key={`dropdown-${idx}`} className="w-60">
                            {header && (
                                <h1 className="mx-3.5 uppercase font-bold text-lg text-caviar border-b-4 border-garden-grove pb-2 mb-6 tracking-wide w-fit flex">
                                    {header}
                                </h1>
                            )}

                            {!isEmpty(items) && (
                                <>
                                    {items.map((item, idx) => (
                                        <Item
                                            key={`item-${idx}`}
                                            {...item}
                                            setNavigationMenuOpen={
                                                setNavigationMenuOpen
                                            }
                                        />
                                    ))}
                                </>
                            )}
                        </div>
                    )
                }
            })}
        </div>
    )
}

const NavLink = ({
    item,
    className = "",
    navigationMenu,
    setNavigationMenu,
    setNavigationMenuOpen,
    navigationMenuReposition,
    navigationMenuLeave,
}) => {
    if (!item.columns) {
        return (
            <li>
                <Link
                    href={item.href ?? "#"}
                    className={clsx(
                        "inline-flex items-center justify-center h-10 px-4 py-2 font-bold transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none hover:bg-arugula w-max",
                        className,
                    )}
                >
                    {item.name}
                </Link>
            </li>
        )
    }

    return (
        <li className={className}>
            <button
                className={clsx(
                    "inline-flex items-center justify-center h-10 px-3 lg:px-4 py-5 text-lg transition-colors focus:outline-none disabled:opacity-50 disabled:pointer-events-none w-max cursor-pointer",
                    navigationMenu === item.name ?
                        "bg-garden-grove border-garden-grove"
                    :   "hover:bg-arugula",
                )}
                onMouseOver={(e) => {
                    setNavigationMenuOpen(true)
                    navigationMenuReposition(e.currentTarget)
                    setNavigationMenu(item.name)
                }}
                onMouseLeave={navigationMenuLeave}
                onClick={() => window.open(item.key, "_self")}
            >
                <span className="font-bold">{item.name}</span>
                <svg
                    className={`relative top-[1px] -ml-1 left-2 size-4 ease-out duration-300 ${
                        navigationMenu === item.name ? "-rotate-180" : ""
                    }`}
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="miter"
                    strokeLinejoin="miter"
                    aria-hidden="true"
                >
                    <polyline points="6 9 12 15 18 9"></polyline>
                </svg>
            </button>
        </li>
    )
}

const MobileNavLink = ({
    item,
    className = "",
    navigationMenu,
    setNavigationMenu,
    setNavigationMenuOpen,
    navigationMenuReposition,
    navigationMenuLeave,
}) => {
    const router = useRouter()
    const links = item.columns
        .flatMap((col) => col.items)
        .filter((link) => link.title !== undefined)
        .map((link) => ({ title: link.title, href: link.href }))

    return (
        <li className={className}>
            <Select
                label={item.name}
                options={links}
                callback={(href) => router.push(href)}
            />
        </li>
    )
}

const Select = ({ label, className = "", options, callback = () => {} }) => {
    const [open, setOpen] = useState(true)
    const [selected, setSelected] = useState(null)

    const list = useClickAway(() => {
        if (open) setOpen(false)
    })

    const select = (option) => {
        setSelected(option)
    }

    useEffect(() => {
        if (open) setOpen(false)

        if (!isEmpty(selected)) callback(selected)
    }, [selected])

    useEffect(() => {
        if (list.current !== null) {
            const rect = list.current.getBoundingClientRect()

            if (rect.right > window.innerWidth) {
                list.current.style.right = "0px"
            }

            if (rect.left < 0) {
                list.current.style.left = "0px"
            }
        }
    }, [open])

    return (
        <>
            <div className={twMerge("relative", className)}>
                <select
                    name={label}
                    className="sr-only absolute top-1/2 left-1/2 -translate-1/2 w-full h-full"
                    onChange={(e) => {
                        if (multiple) {
                            const values = Array.from(
                                e.target.selectedOptions,
                                (opt) => opt.value,
                            )
                            setSelected(values)
                        } else {
                            setSelected([e.target.value])
                        }
                    }}
                >
                    <option default value={undefined}></option>
                    {options.map(({ title, href }, idx) => (
                        <option key={idx} value={href}>
                            {title}
                        </option>
                    ))}
                </select>

                <button
                    name={label}
                    id={label}
                    type="button"
                    onClick={() => {
                        setOpen(true)
                    }}
                    className={clsx(
                        "peer focus:outline-none",
                        "min-h-[38px] text-caviar flex items-center justify-between w-full py-2 text-left bg-arugula cursor-default",
                    )}
                >
                    <span
                        className={clsx(
                            "text-white-snow font-bold w-full transition-transform duration-300 whitespace-nowrap",
                        )}
                    >
                        {label}
                    </span>
                    <span className="flex items-center ml-1 pointer-events-none text-white-snow">
                        <svg
                            className="size-4"
                            xmlns="http://www.w3.org/2000/svg"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="miter"
                            strokeLinejoin="miter"
                            aria-hidden="true"
                        >
                            <polyline points="6 9 12 15 18 9"></polyline>
                        </svg>
                    </span>
                </button>

                {open && (
                    <ul
                        ref={list}
                        className={clsx(
                            "absolute z-30 w-fit mt-1 overflow-auto bg-white-snow max-h-fit focus:outline-none shadow-xl",
                        )}
                    >
                        {options.map((option, idx) => (
                            <li
                                onClick={() => select(option.href)}
                                key={`option-${idx}`}
                                className="relative flex items-center h-full px-4 py-2 text-caviar cursor-default select-none"
                            >
                                <span className="block font-medium whitespace-nowrap">
                                    {option.title}
                                </span>
                            </li>
                        ))}
                    </ul>
                )}
            </div>
        </>
    )
}

const Navigation = ({ links = [] }) => {
    const [navigationMenuOpen, setNavigationMenuOpen] = useState(false)
    const [navigationMenu, setNavigationMenu] = useState("")
    const navigationMenuCloseDelay = 200
    const navigationMenuCloseTimeout = useRef(null)
    const navigationDropdownRef = useRef(null)

    const navigationMenuLeave = () => {
        navigationMenuCloseTimeout.current = setTimeout(() => {
            navigationMenuClose()
        }, navigationMenuCloseDelay)
    }

    const navigationMenuReposition = (navElement) => {
        clearTimeout(navigationMenuCloseTimeout.current)
        if (navigationDropdownRef.current && navElement) {
            navigationDropdownRef.current.style.left = `${navElement.offsetLeft}px`
            navigationDropdownRef.current.style.marginLeft = `${navElement.offsetWidth / 2}px`
        }
    }

    const navigationMenuClearCloseTimeout = () => {
        clearTimeout(navigationMenuCloseTimeout.current)
    }

    const navigationMenuClose = () => {
        setNavigationMenuOpen(false)
        setNavigationMenu("")
    }

    useEffect(() => {
        return () => clearTimeout(navigationMenuCloseTimeout.current)
    }, [])

    return (
        <div className="relative z-40 h-fit my-auto">
            <ul className="flex flex-row items-center justify-center gap-3 lg:gap-8 list-none text-white-snow group w-full">
                {links.map((item, idx) => (
                    <Fragment key={"nav-" + idx}>
                        <MobileNavLink
                            key={"mobile" + item.name}
                            item={item}
                            className="md:hidden"
                            navigationMenu={navigationMenu}
                            setNavigationMenu={setNavigationMenu}
                            setNavigationMenuOpen={setNavigationMenuOpen}
                            navigationMenuReposition={navigationMenuReposition}
                            navigationMenuLeave={navigationMenuLeave}
                        />
                        <NavLink
                            key={item.name}
                            item={item}
                            className="hidden md:block"
                            navigationMenu={navigationMenu}
                            setNavigationMenu={setNavigationMenu}
                            setNavigationMenuOpen={setNavigationMenuOpen}
                            navigationMenuReposition={navigationMenuReposition}
                            navigationMenuLeave={navigationMenuLeave}
                        />
                    </Fragment>
                ))}
            </ul>
            <div
                ref={navigationDropdownRef}
                className={`absolute top-0 pt-3 duration-200 ease-out -translate-x-1/2 translate-y-10 z-50 ${
                    navigationMenuOpen ?
                        "opacity-100 scale-100"
                    :   "opacity-0 scale-90"
                } transition-all`}
                style={{ display: navigationMenuOpen ? "block" : "none" }}
                onMouseOver={navigationMenuClearCloseTimeout}
                onMouseLeave={navigationMenuLeave}
            >
                <div className="flex justify-center w-auto h-auto overflow-hidden bg-white-snow shadow-xl">
                    <Dropdown
                        setNavigationMenuOpen={setNavigationMenuOpen}
                        columns={
                            links.find((item) => item.name === navigationMenu)
                                ?.columns
                        }
                    />
                </div>
            </div>
        </div>
    )
}

export default Navigation
